import { apiEndpoints } from "@/src/config/api/endpoints";
import { api, hydrateApiConfig } from "./base_service";

/**
 * `info_service` — the bootstrap call (`GET /info/`).
 *
 * `/info/` is the first authenticated request: it supplies the runtime config
 * the request interceptor needs (default API version, monitoring base URL)
 * plus the current user/company and the available plans. It
 * sends NO `Accept: version` header (it is the source of the default version,
 * so it cannot depend on it) — hence `{ skipVersion: true }`.
 *
 * Verified live against staging 2026-06-08.
 */

// --- raw (snake_case, as the backend sends it) ------------------------------
export interface BasicPlanRaw {
  id: string;
  name: string;
  brief_description: string;
  amount: string;
  currency_symbol: string;
  call_count: number;
  is_sanctions: boolean;
  is_pep: boolean;
  is_media: boolean;
  is_export: boolean;
  stripe_url: string;
}

export interface ApiInfoRaw {
  default_api_version: string;
  app_version: string;
  environment: string;
  urls: { monitoring_base_url: string; admin_zone_uuid: string };
  user: {
    uuid: string;
    name: string | null;
    email: string;
    /** The session token. Intentionally NOT carried into the camelCase app model. */
    token: string;
    is_staff: boolean;
    enable_monitoring: boolean;
    account_type: string;
    sso_enabled: boolean;
    has_active_plan?: boolean;
    created?: string;
    multi_user_table_enabled?: boolean;
    opt_out?: boolean;
  };
  company: {
    id: number;
    name: string;
    enable_monitoring: boolean;
    adverse_media_enabled: boolean;
    has_active_plan: boolean;
    enforce_sso: boolean;
    pep_allowed?: boolean;
    specific_logo?: string;
  };
  basic_plans: Record<string, BasicPlanRaw>;
}

// --- camelCase app model (consumed by the app shell / route guards) ---------
export interface BasicPlan {
  id: string;
  name: string;
  briefDescription: string;
  amount: string;
  currencySymbol: string;
  callCount: number;
  isSanctions: boolean;
  isPep: boolean;
  isMedia: boolean;
  isExport: boolean;
  stripeUrl: string;
}

export interface ApiInfo {
  defaultApiVersion: string;
  appVersion: string;
  environment: string;
  monitoringBaseUrl: string;
  adminZoneUuid: string;
  user: {
    uuid: string;
    name: string | null;
    email: string;
    isStaff: boolean;
    enableMonitoring: boolean;
    accountType: string;
    ssoEnabled: boolean;
    hasActivePlan: boolean;
    created?: string;
    optOut: boolean;
    // NOTE: `token` is deliberately omitted — it stays in the token store, not
    // spread through app state (ADR-009 least-exposure).
  };
  company: {
    id: number;
    name: string;
    enableMonitoring: boolean;
    adverseMediaEnabled: boolean;
    hasActivePlan: boolean;
    enforceSso: boolean;
    pepAllowed: boolean;
    /** Full S3 URL for the company-specific logo, or undefined to use the default sanctions logo. */
    specificLogoUrl?: string;
  };
  basicPlans: BasicPlan[];
}

function adaptPlan(raw: BasicPlanRaw): BasicPlan {
  return {
    id: raw.id,
    name: raw.name,
    briefDescription: raw.brief_description,
    amount: raw.amount,
    currencySymbol: raw.currency_symbol,
    callCount: raw.call_count,
    isSanctions: raw.is_sanctions,
    isPep: raw.is_pep,
    isMedia: raw.is_media,
    isExport: raw.is_export,
    stripeUrl: raw.stripe_url,
  };
}

export function adaptApiInfo(raw: ApiInfoRaw): ApiInfo {
  return {
    defaultApiVersion: raw.default_api_version,
    appVersion: raw.app_version,
    environment: raw.environment,
    monitoringBaseUrl: raw.urls.monitoring_base_url,
    adminZoneUuid: raw.urls.admin_zone_uuid,
    user: {
      uuid: raw.user.uuid,
      name: raw.user.name,
      email: raw.user.email,
      isStaff: raw.user.is_staff,
      enableMonitoring: raw.user.enable_monitoring,
      accountType: raw.user.account_type,
      ssoEnabled: raw.user.sso_enabled,
      hasActivePlan: raw.user.has_active_plan ?? raw.company.has_active_plan,
      created: raw.user.created,
      optOut: raw.user.opt_out ?? false,
    },
    company: {
      id: raw.company.id,
      name: raw.company.name,
      enableMonitoring: raw.company.enable_monitoring,
      adverseMediaEnabled: raw.company.adverse_media_enabled,
      hasActivePlan: raw.company.has_active_plan,
      enforceSso: raw.company.enforce_sso,
      pepAllowed: raw.company.pep_allowed ?? false,
      specificLogoUrl: raw.company.specific_logo
        ? `https://sanctions-static.s3.amazonaws.com/static/images/${raw.company.specific_logo}`
        : undefined,
    },
    basicPlans: Object.values(raw.basic_plans).map(adaptPlan),
  };
}

/** `GET /info/` — raw payload. Sends no version header. */
export function getInfo(): Promise<ApiInfoRaw> {
  return api.get<ApiInfoRaw>(apiEndpoints.info().get(), { skipVersion: true });
}

/**
 * Fetch `/info/`, hydrate the interceptor's runtime config (version, monitoring
 * base URL), and return the camelCase app model. Call once at app
 * bootstrap, after the token is available, before any version-dependent call.
 */
export async function bootstrapApiConfig(): Promise<ApiInfo> {
  const raw = await getInfo();
  hydrateApiConfig(raw);
  return adaptApiInfo(raw);
}
